import type { IconName } from '../../assets/Icon/Icon';

export type PortSpeed = 10 | 100 | 1000 | 2500 | 10000 | 25000 | 50000;
export type PortStatus = 'connected' | 'disconnected' | 'disabled' | 'error';
export type PortVariant =
  | 'alternative'
  | 'attention'
  | 'neutral'
  | 'positive'
  | 'negative'
  | 'brand'
  | 'disabled';

/**
 * Maps port speeds to their corresponding hardware variants for consistent coloring
 */
export const PORT_SPEED_TO_VARIANT_MAP: Record<PortSpeed, PortVariant> = {
  10: 'attention',
  100: 'attention',
  1000: 'positive',
  2500: 'positive',
  10000: 'alternative',
  25000: 'alternative',
  50000: 'alternative',
};

/**
 * Maps port statuses to their corresponding hardware variants for consistent coloring
 */
export const PORT_STATUS_TO_VARIANT_MAP: Record<PortStatus, PortVariant | null> = {
  connected: null, // Use speed-based variant when connected
  disconnected: 'neutral',
  disabled: 'neutral', // Will be overridden by disabled styling
  error: 'negative', // Note: 'negative' is not in HardwareVariant type, handled specially
};

/**
 * Gets the appropriate variant for a port based on its speed and status
 */
export function getPortVariant(speed: PortSpeed | undefined, status: PortStatus): PortVariant {
  if (status === 'connected') {
    return speed != null ? PORT_SPEED_TO_VARIANT_MAP[speed] : 'positive';
  }

  if (status === 'disabled') {
    return 'disabled';
  }

  if (status === 'error') {
    return 'negative';
  }

  // For disconnected status, use neutral
  return 'neutral';
}

/**
 * Gets the appropriate icon for a port based on its status and properties
 */
export function getPortIcon(uplink?: boolean, blocking?: boolean, poe?: boolean): IconName | null {
  if (blocking) {
    return 'block';
  }

  if (uplink) {
    return 'arrow-up';
  }

  if (poe) {
    return 'lightning';
  }

  return null;
}

/**
 *
 * Gets the appropriate icon for the auxilliary icon based on its properties
 */
export function getAuxiliaryPortIcon(spanMirroring?: boolean): IconName | null {
  if (spanMirroring) {
    return 'span';
  }

  return null;
}

/**
 * Gets the display label for a port speed
 */
export function getPortSpeedLabel(speed: PortSpeed): string {
  if (speed >= 1000) {
    return `${speed / 1000}G`;
  }
  return `${speed}M`;
}

/**
 * Gets the display label for a port status
 */
export function getPortStatusLabel(status: PortStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'disconnected':
      return 'Disconnected';
    case 'disabled':
      return 'Disabled';
    case 'error':
      return 'Error';
    default:
      return 'Unknown';
  }
}

/**
 * Creates legend props for a port based on its properties
 * This ensures consistent styling between Port and Legend components
 */
export function createPortLegendProps(
  speed: PortSpeed,
  status: PortStatus,
  options: {
    uplink?: boolean;
    blocking?: boolean;
    poe?: boolean;
    disabled?: boolean;
    label?: string;
    onClick?: (event: any) => void;
  } = {},
) {
  const variant = getPortVariant(speed, status);
  const icon = getPortIcon(options.uplink, options.blocking, options.poe);

  // Generate label if not provided
  const label = options.label || `${getPortSpeedLabel(speed)} ${getPortStatusLabel(status)}`;

  return {
    variant,
    icon,
    label,
    disabled: options.disabled,
    onClick: options.onClick,
  };
}
